import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, Loader2, Users as UsersIcon, Sparkles, Bell, BellOff, Trash2, Smile, Paperclip, FileText, Download, X, ImageIcon } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useToast } from '../hooks/use-toast';
import { useChatNotifications } from '../context/ChatNotificationsContext';
import { auth, chat, team, getStoredUser } from '../api';

const POLL_MS = 3000;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB — mirror backend
const ALLOWED_MIMES = new Set([
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
]);

function humanFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIconFor(contentType = '') {
  if (contentType.startsWith('image/')) return ImageIcon;
  return FileText;
}

function MessageAttachment({ attachment, onOpenImage }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (!attachment) return null;
  const { fileId, filename, contentType, size, kind } = attachment;
  const url = chat.fileUrl(fileId);
  if (kind === 'image') {
    if (imgFailed) {
      return (
        <div
          className="mt-1.5 inline-flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 max-w-sm"
          data-testid={`attachment-missing-${fileId}`}
        >
          <div className="h-9 w-9 rounded-md bg-stone-200 text-stone-500 grid place-items-center flex-shrink-0">
            <ImageIcon size={16}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-stone-600 truncate">{filename}</div>
            <div className="text-[11px] text-stone-400">Image no longer available</div>
          </div>
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => onOpenImage && onOpenImage(url, filename)}
        className="mt-1.5 inline-block rounded-lg overflow-hidden border border-stone-200 bg-stone-50 hover:border-emerald-300 transition-colors max-w-xs"
        data-testid={`attachment-image-${fileId}`}
      >
        <img
          src={url}
          alt={filename}
          loading="lazy"
          className="block max-h-72 w-auto object-contain bg-stone-100"
          onError={() => setImgFailed(true)}
        />
        <div className="px-3 py-1.5 text-[11px] text-stone-500 flex items-center gap-2 border-t border-stone-100 bg-white">
          <ImageIcon size={11}/>
          <span className="truncate flex-1 text-left">{filename}</span>
          <span className="tabular-nums">{humanFileSize(size)}</span>
        </div>
      </button>
    );
  }
  const Icon = fileIconFor(contentType);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1.5 inline-flex items-center gap-3 rounded-lg border border-stone-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors px-3 py-2.5 max-w-sm"
      data-testid={`attachment-file-${fileId}`}
    >
      <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
        <Icon size={16}/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-stone-900 truncate">{filename}</div>
        <div className="text-[11px] text-stone-500 tabular-nums">{humanFileSize(size)} · click to open</div>
      </div>
      <Download size={14} className="text-stone-400 flex-shrink-0"/>
    </a>
  );
}


// Curated emoji grid organised by category — no third-party dependency
const EMOJI_GROUPS = [
  { label: 'Smileys', emojis: ['😀','😁','😂','🤣','😊','😍','😎','🤔','😅','😉','😭','😡','🙃','😴','🤯','🤗','🤩','😇'] },
  { label: 'Reactions', emojis: ['👍','👎','👏','🙌','🙏','👋','💪','🤝','✌️','🤞','👌','🤌','🫶','🤘'] },
  { label: 'Hearts', emojis: ['❤️','💚','💙','💛','🧡','💜','🖤','🤍','💔','💕','💯','✨'] },
  { label: 'Work', emojis: ['💼','📊','📈','📉','📅','📝','📎','📁','🗂️','✅','❌','⚠️','🚨','⏰','⏳','💰','💵','🧾'] },
  { label: 'Objects', emojis: ['🎉','🎊','🏆','🥇','🚀','🔥','⭐','🎯','💡','☕','🍕','🍰','🎁'] },
];

// Deterministic colour palette per user (sky/emerald tones alternated)
const AVATAR_PALETTE = [
  'bg-emerald-100 text-emerald-900',
  'bg-sky-100 text-sky-900',
  'bg-amber-100 text-amber-900',
  'bg-purple-100 text-purple-900',
  'bg-teal-100 text-teal-900',
  'bg-rose-100 text-rose-900',
  'bg-indigo-100 text-indigo-900',
];

function avatarColor(userId) {
  if (!userId) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function initials(name) {
  if (!name) return 'U';
  return name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function isSameDay(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function dateLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function Chat() {
  const nav = useNavigate();
  const { toast } = useToast();
  const { markRead, mute, toggleMute } = useChatNotifications();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // {file, preview?: blobUrl}
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [imageModal, setImageModal] = useState(null); // {url, name}
  const listRef = useRef(null);
  const textRef = useRef(null);
  const emojiRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const lastTimestampRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const isOwner = user?.firmRole === 'owner';

  const scrollToBottom = (behavior = 'smooth') => {
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  };

  const loadInitial = async () => {
    try {
      const [me, res, tm] = await Promise.all([
        auth.me().catch(() => null),
        chat.list(),
        team.list().catch(() => ({ members: [] })),
      ]);
      if (me) setUser(me);
      const msgs = res.messages || [];
      setMessages(msgs);
      seenIdsRef.current = new Set(msgs.map((m) => m.id));
      lastTimestampRef.current = msgs.length ? msgs[msgs.length - 1].createdAt : res.now;
      setConnectedAt(res.now);
      setTeamMembers(tm.members || []);
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom('auto'), 40);
    }
  };

  const poll = async () => {
    try {
      const res = await chat.list(lastTimestampRef.current || undefined);
      const fresh = (res.messages || []).filter((m) => !seenIdsRef.current.has(m.id));
      if (fresh.length > 0) {
        fresh.forEach((m) => seenIdsRef.current.add(m.id));
        setMessages((prev) => [...prev, ...fresh]);
        lastTimestampRef.current = fresh[fresh.length - 1].createdAt;
        // Auto-scroll if user is near the bottom
        const el = listRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
          setTimeout(() => scrollToBottom(), 30);
        }
        // User is on the chat page — mark everything as read so the global badge clears
        markRead();
      } else if (res.now) {
        lastTimestampRef.current = lastTimestampRef.current || res.now;
      }
    } catch { /* transient failure — next tick */ }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    loadInitial();
    // Clear any pending unread as soon as the page mounts
    markRead();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (loading) return;
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [loading]);

  const clearPendingFile = () => {
    if (pendingFile?.preview) URL.revokeObjectURL(pendingFile.preview);
    setPendingFile(null);
    setUploadProgress(0);
  };

  const stageFile = (file) => {
    if (!file) return;
    if (!ALLOWED_MIMES.has(file.type)) {
      toast({ title: 'File type not allowed', description: 'Images, PDFs, Word, Excel, CSV and text files only.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast({ title: 'File too large', description: 'Max 10 MB per file.', variant: 'destructive' });
      return;
    }
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    if (pendingFile?.preview) URL.revokeObjectURL(pendingFile.preview);
    setPendingFile({ file, preview });
    setUploadProgress(0);
    setTimeout(() => textRef.current?.focus(), 50);
  };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) stageFile(f);
    e.target.value = ''; // allow re-selecting the same file
  };

  const send = async () => {
    const text = draft.trim();
    const file = pendingFile?.file || null;
    if (!text && !file) return;
    if (sending) return;
    setSending(true);
    // Optimistic prepend (text only — attachment appears when the server replies)
    const tempId = `local-${Date.now()}`;
    const now = new Date().toISOString();
    const optimistic = {
      id: tempId,
      userId: user?.id,
      userName: user?.name,
      userRole: user?.firmRole,
      text,
      createdAt: now,
      _pending: true,
      _hasFile: !!file,
    };
    setMessages((prev) => [...prev, optimistic]);
    seenIdsRef.current.add(tempId);
    setDraft('');
    setTimeout(() => scrollToBottom(), 30);
    try {
      let attachment = null;
      if (file) {
        attachment = await chat.upload(file, (p) => setUploadProgress(p));
      }
      const saved = await chat.send(text, attachment);
      seenIdsRef.current.add(saved.id);
      setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));
      lastTimestampRef.current = saved.createdAt;
      clearPendingFile();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setDraft(text); // let user retry
      const detail = err?.response?.data?.detail || 'Something went wrong — try again.';
      toast({ title: file ? 'Upload failed' : 'Send failed', description: detail, variant: 'destructive' });
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          stageFile(f);
          return;
        }
      }
    }
  };

  // Drag & drop on the whole feed
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer?.types?.includes('Files')) return;
    dragCounterRef.current += 1;
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) stageFile(f);
  };

  const insertEmoji = (emoji) => {
    const el = textRef.current;
    if (!el) { setDraft((v) => v + emoji); return; }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      try { el.setSelectionRange(pos, pos); } catch {}
    });
  };

  const deleteMessage = async (m) => {
    if (!m || m._pending) return;
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    setDeletingId(m.id);
    const prev = messages;
    // Optimistic remove
    setMessages((list) => list.filter((x) => x.id !== m.id));
    try {
      await chat.remove(m.id);
      seenIdsRef.current.delete(m.id);
      toast({ title: 'Message deleted' });
    } catch (err) {
      // Rollback
      setMessages(prev);
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [emojiOpen]);

  // Revoke blob preview URL on unmount
  useEffect(() => {
    return () => {
      if (pendingFile?.preview) URL.revokeObjectURL(pendingFile.preview);
    };
    // eslint-disable-next-line
  }, []);

  // Escape closes the image modal
  useEffect(() => {
    if (!imageModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setImageModal(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [imageModal]);

  // Group messages by day + collapse consecutive from same user
  const grouped = useMemo(() => {
    const out = [];
    let lastDayKey = null;
    let lastUserId = null;
    let lastTime = null;
    for (const m of messages) {
      const dayKey = new Date(m.createdAt).toDateString();
      if (dayKey !== lastDayKey) {
        out.push({ type: 'date', label: dateLabel(m.createdAt), key: `d-${dayKey}` });
        lastDayKey = dayKey;
        lastUserId = null;
      }
      const timeGap = lastTime ? (new Date(m.createdAt) - new Date(lastTime)) : Infinity;
      const grouped = lastUserId === m.userId && timeGap < 5 * 60 * 1000;
      out.push({ type: 'msg', m, grouped, key: m.id });
      lastUserId = m.userId;
      lastTime = m.createdAt;
    }
    return out;
  }, [messages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading team chat...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Chat"/>
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar user={user} firm={user?.firm}/>
        <main className="flex-1 min-h-0 p-4 lg:p-8 pb-4 lg:pb-4">
          <div className="h-[calc(100vh-200px)] min-h-[560px] grid lg:grid-cols-[1fr_260px] gap-6">
            {/* Feed */}
            <section
              className="rounded-xl bg-white border border-stone-200 flex flex-col overflow-hidden relative"
              data-testid="chat-feed"
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              {isDragging && (
                <div
                  className="absolute inset-2 z-30 rounded-xl border-2 border-dashed border-emerald-400 bg-emerald-50/90 backdrop-blur-sm grid place-items-center pointer-events-none"
                  data-testid="chat-drop-overlay"
                >
                  <div className="text-center">
                    <div className="mx-auto h-14 w-14 rounded-full bg-white text-emerald-700 grid place-items-center shadow-sm mb-3">
                      <Paperclip size={22}/>
                    </div>
                    <div className="font-serif text-xl font-semibold text-emerald-900">Drop to attach</div>
                    <div className="text-sm text-emerald-800/80 mt-1">Images, PDFs & docs up to 10 MB</div>
                  </div>
                </div>
              )}
              <div className="p-4 border-b border-stone-100 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-700 to-sky-700 grid place-items-center">
                  <MessageSquare size={16} className="text-white"/>
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg font-semibold text-stone-900">#team-chat</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"/>
                    Live · {user?.firm} · {teamMembers.length} teammate{teamMembers.length === 1 ? '' : 's'}
                  </div>
                </div>
                <button
                  onClick={toggleMute}
                  className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors ${mute ? 'border-stone-300 text-stone-500 bg-stone-50 hover:bg-stone-100' : 'border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100'}`}
                  title={mute ? 'Notifications muted — click to enable ding' : 'Notifications on — click to mute'}
                  data-testid="chat-mute-btn"
                >
                  {mute ? <BellOff size={15}/> : <Bell size={15}/>}
                </button>
              </div>

              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="chat-messages">
                {grouped.length === 0 && (
                  <div className="h-full grid place-items-center text-center px-8">
                    <div>
                      <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center mb-3">
                        <Sparkles size={20}/>
                      </div>
                      <div className="font-serif text-lg text-stone-900">Start the conversation</div>
                      <div className="mt-1 text-sm text-stone-500 max-w-sm mx-auto">Say hi to your team, share client updates, or drop a link. Everyone in {user?.firm} can see this channel.</div>
                    </div>
                  </div>
                )}
                {grouped.map((item) => {
                  if (item.type === 'date') {
                    return (
                      <div key={item.key} className="flex items-center gap-3 py-3">
                        <div className="flex-1 h-px bg-stone-100"/>
                        <div className="text-[11px] uppercase tracking-widest text-stone-400 font-medium">{item.label}</div>
                        <div className="flex-1 h-px bg-stone-100"/>
                      </div>
                    );
                  }
                  const m = item.m;
                  const mine = m.userId === user?.id;
                  const isMsgOwner = m.userRole === 'owner';
                  if (item.grouped) {
                    const canDelete = (mine || isOwner) && !m._pending;
                    return (
                      <div key={item.key} className="group pl-11 py-0.5 flex items-start gap-2 hover:bg-stone-50 rounded-md" data-testid={`msg-${m.id}`}>
                        <div className="flex-1 min-w-0">
                          {m.text && (
                            <div className={`text-sm whitespace-pre-wrap break-words ${m._pending ? 'text-stone-400 italic' : 'text-stone-800'}`}>{m.text}</div>
                          )}
                          {m._pending && m._hasFile && (
                            <div className="mt-1 text-[11px] text-stone-400 italic flex items-center gap-1.5"><Loader2 size={11} className="animate-spin"/> Uploading{uploadProgress ? ` ${uploadProgress}%` : '…'}</div>
                          )}
                          <MessageAttachment attachment={m.attachment} onOpenImage={(url, name) => setImageModal({ url, name })}/>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 mt-0.5 flex-shrink-0 flex items-center gap-2">
                          <span className="text-[10px] text-stone-400 tabular-nums">{formatTime(m.createdAt).split(' · ').pop()}</span>
                          {canDelete && (
                            <button
                              onClick={() => deleteMessage(m)}
                              disabled={deletingId === m.id}
                              className="text-stone-400 hover:text-red-600 p-0.5"
                              title={mine ? 'Delete your message' : 'Delete (owner)'}
                              data-testid={`msg-delete-${m.id}`}
                            >
                              <Trash2 size={12}/>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }
                  const canDelete = (mine || isOwner) && !m._pending;
                  return (
                    <div key={item.key} className="flex items-start gap-3 py-2 group hover:bg-stone-50 rounded-md px-1" data-testid={`msg-${m.id}`}>
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className={`text-[11px] font-semibold ${avatarColor(m.userId)}`}>{initials(m.userName)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className={`text-sm font-semibold ${mine ? 'text-emerald-800' : 'text-stone-900'}`}>{m.userName}{mine && ' (you)'}</span>
                          {isMsgOwner && <span className="text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded font-medium">Owner</span>}
                          <span className="text-[11px] text-stone-400 tabular-nums">{formatTime(m.createdAt)}</span>
                          {m._pending && <span className="text-[10px] text-stone-400 italic">sending…</span>}
                        </div>
                        <div className={`mt-0.5 text-sm whitespace-pre-wrap break-words ${m._pending ? 'text-stone-400 italic' : 'text-stone-800'}`}>{m.text}</div>
                        {m._pending && m._hasFile && (
                          <div className="mt-1 text-[11px] text-stone-400 italic flex items-center gap-1.5"><Loader2 size={11} className="animate-spin"/> Uploading{uploadProgress ? ` ${uploadProgress}%` : '…'}</div>
                        )}
                        <MessageAttachment attachment={m.attachment} onOpenImage={(url, name) => setImageModal({ url, name })}/>
                      </div>
                      {canDelete && (
                        <button
                          onClick={() => deleteMessage(m)}
                          disabled={deletingId === m.id}
                          className="opacity-0 group-hover:opacity-100 self-start mt-1 text-stone-400 hover:text-red-600 p-1 flex-shrink-0"
                          title={mine ? 'Delete your message' : 'Delete (owner)'}
                          data-testid={`msg-delete-${m.id}`}
                        >
                          <Trash2 size={13}/>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="p-3 border-t border-stone-100 bg-stone-50/50 relative">
                {emojiOpen && (
                  <div ref={emojiRef} className="absolute bottom-full left-3 mb-2 w-80 max-h-80 overflow-y-auto rounded-xl bg-white border border-stone-200 shadow-xl z-20" data-testid="emoji-picker">
                    {EMOJI_GROUPS.map((g) => (
                      <div key={g.label} className="p-3 border-b border-stone-100 last:border-b-0">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">{g.label}</div>
                        <div className="grid grid-cols-8 gap-1">
                          {g.emojis.map((e) => (
                            <button
                              key={e}
                              type="button"
                              onClick={() => insertEmoji(e)}
                              className="h-8 w-8 rounded-md text-xl grid place-items-center hover:bg-emerald-50 transition-colors"
                              data-testid={`emoji-${e}`}
                              title={e}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {pendingFile && (
                  <div className="mb-2 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 pr-3" data-testid="pending-attachment">
                    {pendingFile.preview ? (
                      <img src={pendingFile.preview} alt="preview" className="h-12 w-12 rounded-md object-cover border border-emerald-200"/>
                    ) : (
                      <div className="h-12 w-12 rounded-md bg-white border border-emerald-200 grid place-items-center text-emerald-700">
                        <FileText size={18}/>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{pendingFile.file.name}</div>
                      <div className="text-[11px] text-stone-500 tabular-nums">
                        {humanFileSize(pendingFile.file.size)}
                        {sending && uploadProgress > 0 && uploadProgress < 100 && ` · uploading ${uploadProgress}%`}
                        {!sending && ' · attached, press send'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearPendingFile}
                      disabled={sending}
                      className="h-7 w-7 rounded-md grid place-items-center text-stone-500 hover:text-red-600 hover:bg-white disabled:opacity-40"
                      title="Remove attachment"
                      data-testid="remove-pending-file-btn"
                    >
                      <X size={14}/>
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    onChange={onFileChange}
                    data-testid="chat-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    className="h-10 w-10 grid place-items-center rounded-lg border border-stone-200 bg-white text-stone-500 hover:text-emerald-700 hover:border-emerald-300 transition-colors flex-shrink-0 disabled:opacity-50"
                    title="Attach a file or image (max 10 MB)"
                    data-testid="chat-attach-btn"
                  >
                    <Paperclip size={16}/>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmojiOpen((v) => !v)}
                    className={`h-10 w-10 grid place-items-center rounded-lg border transition-colors flex-shrink-0 ${emojiOpen ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-stone-200 bg-white text-stone-500 hover:text-emerald-700 hover:border-emerald-300'}`}
                    title="Add emoji"
                    data-testid="emoji-toggle-btn"
                  >
                    <Smile size={16}/>
                  </button>
                  <Textarea
                    ref={textRef}
                    rows={1}
                    placeholder={pendingFile ? 'Add a message (optional)…' : `Message #team-chat — paste or drop a file to attach`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    className="flex-1 min-h-[40px] max-h-[120px] resize-none bg-white"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={send}
                    disabled={sending || (!draft.trim() && !pendingFile)}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white h-10 px-4"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                  </Button>
                </div>
                <div className="mt-1.5 text-[10px] text-stone-400 flex items-center gap-2">
                  Live · polling every {POLL_MS / 1000}s · drop or paste files to attach (max 10 MB)
                  {connectedAt && <>· connected {new Date(connectedAt).toLocaleTimeString()}</>}
                </div>
              </div>
            </section>

            {/* Members panel */}
            <aside className="hidden lg:flex flex-col rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="chat-members">
              <div className="p-4 border-b border-stone-100">
                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1.5"><UsersIcon size={12}/> Team</div>
                <div className="mt-1 font-serif text-base font-semibold text-stone-900">{user?.firm}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {teamMembers.length === 0 && <div className="text-xs text-stone-500 p-3">No teammates yet. Invite from the Team page.</div>}
                {teamMembers.map((m) => (
                  <div key={m.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-stone-50">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className={`text-[10px] font-semibold ${avatarColor(m.id)}`}>{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{m.name}{m.id === user?.id && ' (you)'}</div>
                      <div className="text-[10px] text-stone-500 truncate">{m.firmRole === 'owner' ? 'Owner' : (m.role || 'Teammate')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </main>
      </div>
      {imageModal && (
        <div
          className="fixed inset-0 z-50 bg-stone-950/80 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => setImageModal(null)}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setImageModal(null); }}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
            title="Close"
            data-testid="image-lightbox-close"
          >
            <X size={18}/>
          </button>
          <img
            src={imageModal.url}
            alt={imageModal.name}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
          />
          <a
            href={imageModal.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2"
          >
            <Download size={14}/> {imageModal.name}
          </a>
        </div>
      )}
    </div>
  );
}
